import { IBaseEntityType } from '@app/shared/types';
import { ENTITY_ID_PATH } from '@config/global-constants';
import { IPdfGridView, ViewSupportType, OrderBy } from '../pdf.type';
import { RowNode } from 'ag-grid-community';
import {
  has,
  isNil,
  isFunction,
  isArray,
  reduce,
  split,
  isEmpty,
  orderBy,
  size,
  chain,
  isEqual,
  concat,
  includes,
  filter,
  compact,
  toLower,
  map,
  find,
  uniq,
} from 'lodash';
import { IPdfGridData } from '../pdf-data.type';

const sortAndFilterData = <T = IBaseEntityType>(
  data: T[],
  gridView: IPdfGridView,
  dataValuePath: string = ENTITY_ID_PATH,
  valuePath: string = ENTITY_ID_PATH,
): T[] => {
  const gridApi = gridView?.baseOnGrid?.xuxGridComponent?.gridApi;

  if (isNil(gridApi)) {
    return data;
  }

  const filterAndSortedDataEntityIds = [];

  const forEachMethodMapping = {
    [ViewSupportType.filter]: gridApi.forEachNodeAfterFilter.bind(gridApi),
    [ViewSupportType.sort]: gridApi.forEachNodeAfterFilterAndSort.bind(gridApi),
    [ViewSupportType.sortAndFilter]:
      gridApi.forEachNodeAfterFilterAndSort.bind(gridApi),
  };

  const viewSupport = gridView.viewSupport || ViewSupportType.filter;

  forEachMethodMapping[viewSupport]((rowNode: RowNode) =>
    filterAndSortedDataEntityIds.push(rowNode?.data?.[valuePath]),
  );

  const allValuedDataIds = uniq(compact(filterAndSortedDataEntityIds));

  return isEqual(viewSupport, ViewSupportType.filter)
    ? filter(data, (singleData) =>
        includes(allValuedDataIds, singleData?.[dataValuePath]),
      )
    : map(allValuedDataIds, (key) =>
        find(data, (item) => isEqual(key, item[dataValuePath])),
      );
};

/**
 * sort your grid data through your sort order and caseInsensitive
 *
 * @param data grid data
 * @param order sort order
 * @param caseInsensitive is need case insensitive @default true
 */
const getOrderedData = (
  data: IPdfGridData[],
  order: OrderBy,
  caseInsensitive: boolean = true,
): IPdfGridData[] => {
  if (isNil(order) || size(data) <= 1) {
    return data;
  }

  let orderByCondition = order;

  if (isFunction(orderByCondition)) {
    orderByCondition = orderByCondition();
  }

  let orderFields = [];
  let orderSorts = [];

  if (isArray(orderByCondition)) {
    [orderFields, orderSorts] = reduce(
      orderByCondition,
      ([fields, sorts], condition) => {
        const [field, sort = 'asc'] = split(condition, ' ');

        if (!isEmpty(field)) {
          fields.push(
            caseInsensitive
              ? (rowData: IPdfGridData): string => toLower(rowData?.[field])
              : field,
          );
          sorts.push(sort);
        }

        return [fields, sorts];
      },
      [[], []],
    );
  } else if (has(orderByCondition, 'gridView')) {
    [orderFields, orderSorts] = reduce(
      orderByCondition?.gridView?.sortModel,
      ([fields, sorts], sortModel) => {
        const { colId: field, sort = 'asc' } = sortModel;

        if (!isEmpty(field)) {
          fields.push(
            caseInsensitive
              ? (rowData: IPdfGridData): string => toLower(rowData?.[field])
              : field,
          );
          sorts.push(sort);
        }

        return [fields, sorts];
      },
      [[], []],
    );
  }

  if (isEmpty(orderFields)) {
    return data;
  }

  return (
    chain(data)
      .groupBy({ isPinnedBottom: true })
      .map((groupedData) => {
        // is pinned bottom data, not need order
        if (isEqual(groupedData?.[0]?.isPinnedBottom, true)) {
          return groupedData;
        }

        return orderBy(groupedData, orderFields, orderSorts);
      })
      // concat ordered data with pinned data
      .reduce((pre, cur) => concat(pre, cur), [])
      .value()
  );
};

export { sortAndFilterData, getOrderedData };
